import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuthStore } from '../lib/auth-store';
import { useOperations } from '../lib/landings';
import { EmptyState } from '../components/ui/EmptyState';
import {
  LandingPage,
  KPIRow,
  CardsRow,
  LandingSkeleton,
  LandingError,
  LandingGuard,
  KPICard,
  LandingCard,
  LandingCardLine,
  ModuleGatedCard,
} from '../components/portal-shell';
import { formatDate, timeAgo } from '../lib/format';

export const Route = createFileRoute('/_authed/operations')({
  component: OperationsPage,
});

function OperationsPage() {
  return (
    <LandingGuard landingPath="/operations">
      <OperationsContent />
    </LandingGuard>
  );
}

const BAND_PILL: Record<string, string> = {
  top: 'bg-[color:var(--color-success)]/15 text-[color:var(--color-success)] border-[color:var(--color-success)]/30',
  medium: 'bg-gold-100/50 text-gold-900 border-gold-500/30 dark:bg-gold-700/20 dark:text-gold-100',
  average: 'bg-[color:var(--color-warning)]/15 text-[color:var(--color-warning)] border-[color:var(--color-warning)]/30',
  low: 'bg-[color:var(--color-danger)]/15 text-[color:var(--color-danger)] border-[color:var(--color-danger)]/30',
};

function BandPill({ band }: { band: string }) {
  const cls = BAND_PILL[band.toLowerCase()] ?? BAND_PILL.average;
  return (
    <span
      className={
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ' +
        cls
      }
    >
      {band}
    </span>
  );
}

function OperationsContent() {
  const user = useAuthStore((s) => s.user);
  const query = useOperations({ enabled: !!user });

  if (query.isLoading || (!query.data && !query.isError)) {
    return (
      <LandingPage
        title="Operations"
        meta="Today's queue · Academic Admin"
        intro="Approvals waiting, exceptions to handle, and what's happening across the academy."
      >
        <LandingSkeleton />
      </LandingPage>
    );
  }
  if (query.isError || !query.data) {
    return (
      <LandingPage title="Operations">
        <LandingError error={query.error} onRetry={() => query.refetch()} />
      </LandingPage>
    );
  }

  const { data } = query.data;
  const hasAnnouncements = data.recentAnnouncements.length > 0;

  return (
    <LandingPage
      title="Operations"
      meta={user ? `${user.displayName} · Academic Admin` : 'Academic Admin'}
      intro="Approvals waiting, exceptions to handle, and what's happening across the academy."
    >
      <KPIRow>
        <KPICard
          label="New applications"
          value={data.newApplicationsCount ?? '—'}
          footnote={
            data.newApplicationsCount === null
              ? 'Hiring module not enabled yet'
              : data.newApplicationsCount === 0
                ? 'No new applicants'
                : 'Waiting for aptitude review'
          }
          newFor="/hr/applications"
        />
        <KPICard
          label="Attendance exceptions"
          value={data.attendanceExceptionsToday}
          footnote="Today"
        />
        <KPICard
          label="Weekly sheets to review"
          value={data.weeklySheetsToReview}
          footnote="Across all teachers"
        />
        <KPICard
          label="At-risk batches"
          value={data.atRiskBatchesCount}
          footnote={data.atRiskBatchesCount === 0 ? 'Healthy' : 'Needs attention'}
          tone={data.atRiskBatchesCount === 0 ? 'success' : 'warning'}
        />
      </KPIRow>

      {data.hiringPipelineDigest ? (
        data.hiringPipelineDigest.length > 0 ? (
          <LandingCard title="Hiring pipeline" newFor="/hr/applications" className="md:col-span-2">
            {data.hiringPipelineDigest.map((c, idx, arr) => (
              <LandingCardLine key={c.candidateId} last={idx === arr.length - 1}>
                <span className="font-medium">{c.name}</span> — {c.stage}
                <span className="text-[color:var(--color-text-muted)]">
                  {' '}
                  · {timeAgo(c.lastEventAt)}
                </span>
              </LandingCardLine>
            ))}
          </LandingCard>
        ) : (
          <LandingCard title="Hiring pipeline" newFor="/hr/applications" className="md:col-span-2">
            <EmptyState
              title="Pipeline empty"
              description="No candidates are in flight right now. They'll show up here once they apply."
              className="py-6"
            />
          </LandingCard>
        )
      ) : (
        <ModuleGatedCard
          title="Hiring pipeline"
          variant="full"
          description="The Hiring module is being rolled out. Applications, interviews, demos and probation will surface here when it's enabled."
        />
      )}

      <CardsRow>
        <LandingCard title="Recent announcements">
          {hasAnnouncements ? (
            data.recentAnnouncements.map((a, idx, arr) => (
              <LandingCardLine key={a.id} last={idx === arr.length - 1}>
                <span className="font-medium">{a.title}</span>
                {a.publishedAt ? (
                  <span className="text-[color:var(--color-text-muted)]">
                    {' '}
                    · published {timeAgo(a.publishedAt)}
                  </span>
                ) : (
                  <span className="text-[color:var(--color-text-muted)]"> · draft</span>
                )}
              </LandingCardLine>
            ))
          ) : (
            <EmptyState
              title="No recent announcements"
              description="Compose one from the Announcements page."
              className="py-6"
            />
          )}
        </LandingCard>

        {data.teacherPerformanceHighlights ? (
          <LandingCard
            title="Teacher performance highlights"
            newFor="/hr/performance"
            titleAction={
              <Link
                to="/hr/performance"
                className="text-xs font-medium text-gold-700 dark:text-gold-300 underline"
              >
                Open
              </Link>
            }
          >
            {data.teacherPerformanceHighlights.top.length === 0 &&
            data.teacherPerformanceHighlights.bottom.length === 0 ? (
              <EmptyState
                title="No data yet"
                description="Highlights appear after the first TMA snapshot."
                className="py-6"
              />
            ) : (
              <>
                {data.teacherPerformanceHighlights.top.map((t, idx, arr) => (
                  <LandingCardLine
                    key={t.teacherId}
                    last={
                      idx === arr.length - 1 &&
                      data.teacherPerformanceHighlights!.bottom.length === 0
                    }
                  >
                    <BandPill band={t.band} />
                    <span className="ml-2 font-medium">{t.name}</span>
                    <span className="text-[color:var(--color-text-muted)] tabular-nums">
                      {' '}
                      · {t.score.toFixed(0)}
                    </span>
                  </LandingCardLine>
                ))}
                {data.teacherPerformanceHighlights.bottom.map((t, idx, arr) => (
                  <LandingCardLine key={t.teacherId} last={idx === arr.length - 1}>
                    <BandPill band={t.band} />
                    <span className="ml-2 font-medium">{t.name}</span>
                    <span className="text-[color:var(--color-text-muted)] tabular-nums">
                      {' '}
                      · {t.score.toFixed(0)}
                    </span>
                  </LandingCardLine>
                ))}
              </>
            )}
          </LandingCard>
        ) : (
          <ModuleGatedCard
            title="Teacher performance highlights"
            description="The TMA module runs nightly and surfaces top and bottom performers here when it's enabled."
          />
        )}
      </CardsRow>
      <span className="sr-only">{formatDate(new Date().toISOString())}</span>
    </LandingPage>
  );
}
